import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import { apiFetch } from '../../utils/api';
import LoadingSpinner from "../LoadingSpinner";
import Snackbar from "../Snackbar";
import AddTaskForm from "../AddTaskForm";
import { useGlobalState } from "../../contexts/GlobalContext";
import "../../Stylesheets/Tasks.css";

const PIPELINE_STAGES = [
  'New',
  'Attempting Contact',
  'Connected',
  'Qualified',
  'Proposal or Showing',
  'Negotiation',
  'Under Contract',
  'Closed',
  'Lost',
];

const normalizePipelineStage = (stage) => {
  const matchedStage = PIPELINE_STAGES.find(
    (candidate) => candidate.toLowerCase() === String(stage || '').toLowerCase()
  );
  return matchedStage || 'New';
};

function ContactDetails({ contactData, setContactData }) {
  const [fullData, setFullData] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingTask, setEditingTask] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showActivityNoteModal, setShowActivityNoteModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'success' });
  const [contactNote, setContactNote] = useState('');
  const [contactMethod, setContactMethod] = useState('Call');
  const [loggingContact, setLoggingContact] = useState(false);
  const [updatingStage, setUpdatingStage] = useState(false);
  const navigate = useNavigate(); 
  const { state, fetchContacts } = useGlobalState();

  const contactId = contactData?.id;

  const getTaskId = (task) => {
    if (!task) return null;
    return (
      task.task_id ??
      task.taskId ??
      task.taskID ??
      task.id ??
      null
    );
  };

  const normalizeTaskUrgency = (urgency) => {
    const normalized = String(urgency || "low").toLowerCase();
    return ["high", "medium", "low"].includes(normalized) ? normalized : "low";
  };

  const getTaskType = (task) => task?.taskType ?? task?.task_type ?? "Task";

  const getTaskDescription = (task) =>
    task?.taskDescription ?? task?.task_description ?? "";

const getTaskDueDate = (task) => task?.dueDate ?? task?.due_date ?? null;
const sortTasksByDueDateAsc = (items) => {
  const toTimestamp = (task) => {
    const raw = getTaskDueDate(task);
    if (!raw) return Number.POSITIVE_INFINITY;
    const ts = new Date(raw).getTime();
    return Number.isNaN(ts) ? Number.POSITIVE_INFINITY : ts;
  };

  return [...items].sort((a, b) => toTimestamp(a) - toTimestamp(b));
};

  const buildTaskState = (task) => {
    const taskId = getTaskId(task);
    const type = getTaskType(task);
    const description = getTaskDescription(task);
    const dueDate = getTaskDueDate(task);
    const urgency = normalizeTaskUrgency(task?.urgency);
    const status = task?.status ?? "open";

    return {
      ...task,
      taskId,
      task_id: taskId,
      taskID: taskId,
      taskType: type,
      task_type: type,
      taskDescription: description,
      task_description: description,
      dueDate,
      due_date: dueDate,
      urgency,
      status,
    };
  };

  const createFallbackData = (contact) => {
    if (!contact) return {};

    if (contact.first_name || contact.last_name) {
      return { ...contact };
    }

    const name = typeof contact.name === "string" ? contact.name.trim() : "";
    if (!name) {
      return { ...contact };
    }

    const [firstName, ...rest] = name.split(/\s+/);
    return {
      ...contact,
      first_name: contact.first_name ?? firstName ?? "",
      last_name: contact.last_name ?? rest.join(" "),
    };
  };

  useEffect(() => {
    const fallbackData = createFallbackData(contactData);

    if (!contactId) {
      setFullData(fallbackData);
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchFullData = async () => {
      setLoading(true);
      setError(null);
      if (Object.keys(fallbackData).length > 0) {
        setFullData((prev) => {
          if (prev && prev.id === fallbackData.id) {
            return prev;
          }
          return fallbackData;
        });
      }
      const token = localStorage.getItem("token");

      if (!token) {
        if (!isMounted) return;
        setFullData(fallbackData);
        setLoading(false);
        setSnackbar({
          show: true,
          message: "Limited details shown. Log in again to see the latest data.",
          type: "warning",
        });
        return;
      }

      try {
        const result = await apiFetch("getFullContactDetails", {
          queryParams: { contact_id: contactId }
        });
        if (result.success) {
          if (!isMounted) return;
          setFullData({
          ...result.contact,
          ...result.preferences,
          ...(result.transactions || {}),
          properties: result.properties || [],
          tasks: result.tasks || [],
          activities: result.activities || []
        });
        } else {
          throw new Error(result.message || "Failed to fetch contact details");
        }
      } catch (err) {
        if (!isMounted) return;
        if (err.message === "Invalid token" || err.message === "Unauthorized") {
          setFullData(fallbackData);
          setSnackbar({
            show: true,
            message: "Session expired. Showing cached contact info.",
            type: "warning",
          });
          setError(null);
        } else {
          setError(err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
	

    fetchFullData();

    return () => {
      isMounted = false;
    };
  }, [contactId, contactData]);

  // ✅ Log updated state separately AFTER it actually updates
useEffect(() => {
  if (Object.keys(fullData).length > 0) {
  }
}, [fullData]);

  const displayFullName = [fullData.first_name, fullData.last_name]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean)
    .join(" ") || fullData.name || "";

  const handleAddListing = () => {
    const contactPayload = {
      id: contactId ?? fullData.id,
      contact_id: contactId ?? fullData.contact_id ?? fullData.id,
      first_name: fullData.first_name ?? fullData.firstName ?? "",
      last_name: fullData.last_name ?? fullData.lastName ?? "",
      phone: fullData.phone ?? "",
      email: fullData.email ?? "",
      mail_street: fullData.mail_street ?? "",
      mail_city: fullData.mail_city ?? "",
      mail_state: fullData.mail_state ?? "",
      mail_zip: fullData.mail_zip ?? "",
    };

    navigate("/form", {
      state: {
        role: "re",
        contact: contactPayload,
      },
    });
  };

  const renderField = (label, value) => {
    if (value !== null && value !== undefined && value !== "") {
      return (
        <div className="col-md-6 mb-2">
          <label className="form-label fw-bold">{label}</label>
          <p className="form-control-plaintext">{String(value)}</p>
        </div>
      );
    }
    return null;
  };

  const renderSection = (sectionLabel, fields) => {
    const nonEmptyFields = fields.filter(([label, value]) => value != null && value !== "");
    if (nonEmptyFields.length === 0) return null;

    return (
      <div className="mb-4">
        <h4 className="border-bottom pb-2">{sectionLabel}</h4>
        <div className="row">
          {nonEmptyFields.map(([label, value]) => renderField(label, value))}
        </div>
      </div>
    );
  };
  const renderMailingAddress = () => {
    const parts = [fullData.mail_street, fullData.mail_city, fullData.mail_state, fullData.mail_zip];
    return parts.filter(Boolean).join(', ');
  };

  const formatDateTime = (value) => {
    if (!value) return null;
    const dt = new Date(value);
    return Number.isNaN(dt.getTime()) ? value : dt.toLocaleString();
  };

  const getActivityPreview = (note, maxLength = 72) => {
    const normalized = String(note || '').replace(/\s+/g, ' ').trim();
    if (normalized.length <= maxLength) {
      return normalized;
    }
    return `${normalized.slice(0, maxLength - 1)}…`;
  };

  const hasLongActivityNote = (note, maxLength = 72) =>
    String(note || '').replace(/\s+/g, ' ').trim().length > maxLength;

const openTasks = Array.isArray(fullData.tasks)
    ? sortTasksByDueDateAsc(
        fullData.tasks.map(buildTaskState).filter((task) => task.status === "open")
      )
    : [];

  const contactActivities = Array.isArray(fullData.activities) ? fullData.activities : [];

  const handleTaskAdded = (newTask) => {
    const normalizedTask = buildTaskState(newTask);
    setFullData((prev) => ({
      ...prev,
      tasks: [...(prev.tasks || []), normalizedTask],
    }));
    setContactData?.((prev) => ({
      ...(prev || {}),
      tasks: [...((prev && prev.tasks) || []), normalizedTask],
    }));
  };

  const handleLogContactActivity = async () => {
    if (!contactId) {
      setSnackbar({ show: true, message: '❌ Missing contact ID.', type: 'danger' });
      return;
    }

    setLoggingContact(true);
    try {
      const result = await apiFetch('logContactActivity', {
        method: 'POST',
        body: JSON.stringify({
          contact_id: contactId,
          contact_method: contactMethod,
          note: contactNote,
        }),
      });

      const activity = result.activity || {
        contact_method: contactMethod,
        note: contactNote,
        created_at: result.last_contacted_at,
      };

      setFullData((prev) => ({
        ...prev,
        last_contacted_at: result.last_contacted_at || prev.last_contacted_at,
        notes: result.note_entry
          ? [prev.notes, result.note_entry].filter(Boolean).join('\n')
          : prev.notes,
        activities: [activity, ...(prev.activities || [])],
      }));
      setContactData?.((prev) => ({
        ...(prev || {}),
        last_contacted_at: result.last_contacted_at || prev?.last_contacted_at,
        notes: result.note_entry
          ? [prev?.notes, result.note_entry].filter(Boolean).join('\n')
          : prev?.notes,
      }));
      setContactNote('');
      setSnackbar({ show: true, message: '✅ Contact activity logged.', type: 'success' });
    } catch (err) {
      setSnackbar({ show: true, message: `❌ ${err.message}`, type: 'danger' });
    } finally {
      setLoggingContact(false);
    }
  };

  const handleStageChange = async (event) => {
    const prospectStage = event.target.value;
    const stageContactId = contactId ?? fullData.id;
    const previousStage = normalizePipelineStage(fullData.prospect_stage);
    if (!stageContactId || prospectStage === previousStage) return;

    const changedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    setUpdatingStage(true);
    setFullData((prev) => ({
      ...prev,
      prospect_stage: prospectStage,
      stage_changed_at: changedAt,
    }));

    try {
      const result = await apiFetch('editContact', {
        method: 'POST',
        body: JSON.stringify({
          contact_id: stageContactId,
          prospect_stage: prospectStage,
          stage_changed_at: changedAt,
          won_at: prospectStage === 'Closed' ? changedAt : null,
          lost_at: prospectStage === 'Lost' ? changedAt : null,
        }),
      });

      if (!result.success) throw new Error(result.message || 'Unable to update the pipeline stage.');

      setContactData?.((prev) => ({
        ...(prev || {}),
        prospect_stage: prospectStage,
        stage_changed_at: changedAt,
      }));
      await fetchContacts();
      setSnackbar({ show: true, message: 'Pipeline stage updated.', type: 'success' });
    } catch (err) {
      setFullData((prev) => ({ ...prev, prospect_stage: previousStage }));
      setSnackbar({ show: true, message: `Unable to update pipeline stage: ${err.message}`, type: 'danger' });
    } finally {
      setUpdatingStage(false);
    }
  };


  const handleTaskUpdate = async (taskId, updatedTask) => {
    if (taskId == null) {
      setSnackbar({ show: true, message: "❌ Unable to update task: Missing task ID.", type: "danger" });
      return;
    }

    const normalizedTask = buildTaskState({ ...updatedTask, task_id: taskId });
    try {
      const result = await apiFetch("updateTask", {
        method: "POST",
        body: JSON.stringify({
          ...normalizedTask,
          task_id: normalizedTask.task_id,
          task_type: normalizedTask.task_type,
          task_description: normalizedTask.task_description,
          due_date: normalizedTask.due_date,
          urgency: normalizedTask.urgency,
          status: normalizedTask.status,
        })
      });

      if (!result.success) {
        setSnackbar({ show: true, message: `❌ Failed to update task: ${result.message}` , type: 'danger' });
        return;
      }

      const updatedTasks = (fullData.tasks || []).map((t) =>
        String(getTaskId(t)) === String(taskId) ? normalizedTask : t
      );
      setFullData({ ...fullData, tasks: updatedTasks });
      setEditingTask(null);
      setShowModal(false);
    } catch (err) {
      console.error("Error updating task:", err);
      setSnackbar({ show: true, message: `❌ Error updating task: ${err.message}`, type: 'danger' });
    }
  };

  
  const handleTaskDelete = async (taskId) => {
    if (taskId == null) {
      setSnackbar({ show: true, message: "❌ Unable to delete task: Missing task ID.", type: "danger" });
      return;
    }
    try {
      const result = await apiFetch("deleteTask", {
        method: "POST",
        body: JSON.stringify({ task_id: taskId }),
      });

      if (!result.success) {
        setSnackbar({ show: true, message: `❌ Failed to delete task: ${result.message}`, type: 'danger' });
        return;
      }

      const updatedTasks = (fullData.tasks || []).filter(
        (t) => String(getTaskId(t)) !== String(taskId)
      );
      setFullData({ ...fullData, tasks: updatedTasks });
    } catch (err) {
      console.error("Error deleting task:", err);
      setSnackbar({ show: true, message: `❌ Error deleting task: ${err.message}`, type: 'danger' });
    }
  };
  

  if (loading) return <LoadingSpinner message="Loading contact details…" />;
  if (error) return <p className="text-danger">Error: {error}</p>;
  
  return (
    <div className="container mt-4">
      <Snackbar
        show={snackbar.show}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar((s) => ({ ...s, show: false }))}
      />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="mb-0">Contact Details</h1>
        {contactId && (
          <button className="btn btn-primary" onClick={handleAddListing}>
            Add Listing
          </button>
        )}
      </div>

      {renderSection("Basic Info", [
        ["Full Name", displayFullName],
        ["Email", fullData.email],
        ["Phone", fullData.phone],
        ["Mailing Address", renderMailingAddress()],
        ["Role", fullData.role],
        ["Priority", fullData.priority],
        ["Preferred Contact Method", fullData.preferred_contact_method],
        ["Created At", formatDateTime(fullData.created_at ?? fullData.createdAt)],
      ])}

      {renderSection("Sales Profile", [
        ["Company Name", fullData.company_name],
        ["Job Title", fullData.job_title],
        ["Industry", fullData.industry],
        ["Decision Role", fullData.decision_role],
        ["Urgency Level", fullData.urgency_level],
        ["Estimated Value", fullData.estimated_value],
        ["Next Step", fullData.next_step],
        ["Next Follow-Up", formatDateTime(fullData.next_follow_up_at)],
        ["Last Contacted", formatDateTime(fullData.last_contacted_at)],
        ["Budget", fullData.budget],
        ["Timeline To Purchase", fullData.timeline_to_purchase],
        ["Best Time To Contact", fullData.time],
        ["Pain Points", fullData.pain_points],
        ["Goals", fullData.goals],
        ["Motivation", fullData.motivation],
        ["Current Solution", fullData.current_solution],
        ["Notes", fullData.notes],
        ["LLC(s)", fullData.llcs],
      ])}

      <div className="mb-4">
        <h4 className="border-bottom pb-2">Pipeline</h4>
        <div className="row">
          <div className="col-md-6 mb-2">
            <label className="form-label fw-bold" htmlFor="contact-pipeline-stage">Prospect Stage</label>
            <select
              className="form-select"
              id="contact-pipeline-stage"
              value={normalizePipelineStage(fullData.prospect_stage)}
              onChange={handleStageChange}
              disabled={updatingStage || !(contactId ?? fullData.id)}
            >
              {PIPELINE_STAGES.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
            </select>
          </div>
        </div>
      </div>

      {renderSection("Preferences", [
        ["Preapproval Status", fullData.preapproval_status],
        ["Financing Status", fullData.financing_status],
        ["Property Type", fullData.property_type],
        ["Payment Type", fullData.payment_type],
        ["Wants/Needs", fullData.wants_needs],
        ["Location", fullData.location]
      ])}

      {renderSection("Transaction Info", [
        ["Offer", fullData.offer],
        ["Due Diligence", fullData.due_diligence],
        ["Days for Closing", fullData.days_for_closing],
        ["Title Company", fullData.title_company],
        ["Escrow Amount", fullData.escrow_amount],
        ["Commission", fullData.commission],
        ["Seller B", fullData.seller_b]
      ])}

      {renderSection("Future Use", [["Future Use", fullData.future_use]])}

      <div className="row g-3 mb-4">
        <div className="col-12 col-xl-6">
          <AddTaskForm onAddTask={handleTaskAdded} contactId={contactId} />
        </div>
        <div className="col-12 col-xl-6">
          <div className="container bg-light shadow-sm rounded p-2 h-100">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="text-center mb-0">Contact Activity</h6>
              <small className="text-muted">
                Last contacted: {formatDateTime(fullData.last_contacted_at) || 'Never'}
              </small>
            </div>
            <div className="row g-2 align-items-end mb-3">
              <div className="col-sm-7">
                <label className="form-label form-label-sm mb-1">Note</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Example: Spoke on phone not interested at this time."
                  value={contactNote}
                  onChange={(e) => setContactNote(e.target.value)}
                />
              </div>
              <div className="col-sm-3">
                <label className="form-label form-label-sm mb-1">Contact Method</label>
                <select
                  className="form-select form-select-sm"
                  value={contactMethod}
                  onChange={(e) => setContactMethod(e.target.value)}
                >
                  <option value="Call">Call</option>
                  <option value="Text">Text</option>
                  <option value="Email">Email</option>
                  <option value="In Person">In Person</option>
                  <option value="Voicemail">Voicemail</option>
                  <option value="Video Call">Video Call</option>
                  <option value="Social Media">Social Media</option>
                </select>
              </div>
              <div className="col-sm-2">
                <button
                  type="button"
                  className="btn btn-primary btn-sm w-100"
                  onClick={handleLogContactActivity}
                  disabled={loggingContact}
                >
                  {loggingContact ? 'Saving…' : 'Log Contact'}
                </button>
              </div>
            </div>
            <div className="border rounded bg-white p-2" style={{ minHeight: '252px', maxHeight: '252px', overflowY: 'auto' }}>
              {contactActivities.length > 0 ? (
                <div className="d-flex flex-column">
                  {contactActivities.map((activity, index) => (
                    <div
                      key={activity.id ?? `${activity.contact_method}-${activity.created_at}-${index}`}
                      className="border-bottom py-2"
                    >
                      <div className="d-flex align-items-center gap-2 small flex-nowrap">
                        <span className="fw-semibold text-nowrap">{activity.contact_method}</span>
                        <span className="text-muted text-nowrap">{formatDateTime(activity.created_at)}</span>
                        {activity.note ? (
                          <>
                            <span
                              className="text-truncate flex-grow-1"
                              title={activity.note}
                              style={{ minWidth: 0 }}
                            >
                              {getActivityPreview(activity.note)}
                            </span>
                            {hasLongActivityNote(activity.note) && (
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-nowrap"
                                onClick={() => {
                                  setSelectedActivity(activity);
                                  setShowActivityNoteModal(true);
                                }}
                              >
                                More
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="text-muted fst-italic">No note</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted mb-0">No contact activity logged yet.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Render associated properties */}
      {Array.isArray(fullData.properties) && fullData.properties.length > 0 && (
        <div className="mb-4">
          <h4 className="border-bottom pb-2">Linked Properties</h4>
          {fullData.properties.map((prop, idx) => {
            const propertyId = prop.id ?? prop.property_id;
            const propertyAddress = [prop.street_address, prop.city, prop.state, prop.zip_code]
              .filter(Boolean)
              .join(', ');
            const propertyTypeLabel = [prop.property_type, prop.property_subtype]
              .filter(Boolean)
              .join(' - ');

            const currentUser = state?.user;
            const canEditLinkedProperty = Boolean(currentUser && propertyId);

            return (
              <div
                key={propertyId || idx}
                className="card mb-3 shadow-sm"
                onClick={() => propertyId && navigate(`/property/${propertyId}`)}
                role="button"
              >
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <h5 className="card-title mb-0">
                      {propertyAddress || 'Property'}
                    </h5>
                    {canEditLinkedProperty && propertyId && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/edit/${propertyId}`);
                        }}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  <div className="row mt-2">
                    {renderField("Property Type", propertyTypeLabel)}
                    {renderField("Bedrooms", prop.bedrooms)}
                    {renderField("Bathrooms", prop.bathrooms)}
                    {renderField("Acreage", prop.acreage)}
                    {renderField("Year Built", prop.year_built)}
                    {renderField("Parcel Number", prop.parcel_number)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Render Tasks */}
      <h4 className="border-bottom pb-2">Tasks</h4>
      {openTasks.length > 0 ? (
        <div className="row">
          {openTasks.map((task) => {
            const taskIdValue = task.task_id;
            const dueDateDisplay = task.due_date
              ? new Date(task.due_date).toLocaleDateString()
              : null;
            const actionsDisabled = taskIdValue == null;

            return (
              <div
                className="col-lg-4 col-md-6 mb-3"
                key={taskIdValue ?? `${task.task_type}-${task.task_description}`}
              >
                <div className="card shadow-sm h-100">
                  <div className={`task-card-header task-urgency-${task.urgency}`}>
                    <span className="task-card-title">
                      {task.task_type || "Task"}
                    </span>
                    <span className="task-card-urgency">
                      {task.urgency}
                    </span>
                  </div>
                  <div className="task-card-body">
                    <p className="task-card-description">
                      {task.task_description || "No description provided."}
                    </p>
                    {dueDateDisplay && (
                      <small className="text-muted d-block">
                        Due: {dueDateDisplay}
                      </small>
                    )}
                  </div>
                  <div className="card-footer bg-transparent border-top-0 d-flex justify-content-end gap-1">
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() =>
                        handleTaskUpdate(taskIdValue, { ...task, status: "closed" })
                      }
                      disabled={actionsDisabled}
                      title="Mark complete"
                    >
                      ✓
                    </button>
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => {
                        setEditingTask(task);
                        setShowModal(true);
                      }}
                      disabled={actionsDisabled}
                      title="Edit task"
                    >
                      ✎
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleTaskDelete(taskIdValue)}
                      disabled={actionsDisabled}
                      title="Delete task"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-muted">No tasks assigned.</p>
      )}

      {/* Modal for editing task */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingTask && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleTaskUpdate(editingTask.task_id, editingTask);
              }}
            >
              <label className="form-label">Task Type</label>
              <select
                className="form-control mb-2"
                value={editingTask.taskType}
                onChange={(e) =>
                  setEditingTask((prev) => ({
                    ...prev,
                    taskType: e.target.value,
                    task_type: e.target.value,
                  }))
                }
              >
                <option value="call">Call</option>
                <option value="text">Text</option>
                <option value="email">Email</option>
                <option value="appointment">Appointment</option>
              </select>

              <label className="form-label">Description</label>
              <input
                className="form-control mb-2"
                value={editingTask.task_description}
                onChange={(e) =>
                  setEditingTask((prev) => ({
                    ...prev,
                    task_description: e.target.value,
                    taskDescription: e.target.value,
                  }))
                }
              />

              <label className="form-label">Due Date</label>
              <input
                type="date"
                className="form-control mb-2"
                value={editingTask.due_date}
                onChange={(e) =>
                  setEditingTask((prev) => ({
                    ...prev,
                    due_date: e.target.value,
                    dueDate: e.target.value,
                  }))
                }
              />

              <label className="form-label">Urgency</label>
              <select
                className="form-control mb-2"
                value={editingTask.urgency}
                onChange={(e) =>
                  setEditingTask((prev) => ({
                    ...prev,
                    urgency: normalizeTaskUrgency(e.target.value),
                  }))
                }
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>

              <button className="btn btn-primary w-100 mt-3" type="submit">
                Save Changes
              </button>
            </form>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showActivityNoteModal}
        onHide={() => {
          setShowActivityNoteModal(false);
          setSelectedActivity(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Activity Note</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedActivity ? (
            <>
              <div className="mb-2">
                <strong>{selectedActivity.contact_method}</strong>
                <div className="text-muted small">{formatDateTime(selectedActivity.created_at)}</div>
              </div>
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {selectedActivity.note || 'No note'}
              </div>
            </>
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default ContactDetails;
